import React, {ChangeEvent, ReactNode, useCallback, useEffect, useState} from 'react'
import { connect } from 'react-redux'
import { IAppState } from 'core/store/store'
import styles from '../update-services/UpdateServices.module.scss'
import { Translate } from 'react-localize-redux'
import {
  Button,
  ButtonType,
  DatePicker,
  Form,
  FormFieldSize,
  FormGroup,
  Input,
  ModalTypes,
  Select,
  StandardModal,
  SubmitButton
} from '@digicert/dcone-common-ui'
import { translate } from 'shared/helpers/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCopy } from '@fortawesome/pro-light-svg-icons'
import {generatePassword} from 'shared/helpers/generatePassword'
import PKI from 'secure-pages/client-auth/certificate/PKI'
import { fetchCertificate } from 'secure-pages/client-auth/certificate/Certificate.action'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc);
import { fetchClientAuthCerts } from 'secure-pages/client-auth/ClientAuth.actions'
import { ISort } from 'core/actions/Actions.interfaces'
import { LIMIT_MAX } from 'core/utils/constants'
import {IconProp} from "@fortawesome/fontawesome-svg-core";

export const CreateClientAuthCertificate = (
  { onCancel, fetchCertificate, user, certificates, setCertificates, fetchClientAuthCerts }) => {
  const [name, setName] = useState<string>('')
  const [nameMaxLengthError, setNameMaxLengthError] = useState(false)
  const [nameUniqueError, setNameUniqueError] = useState(false)
  const [date, setDate] = useState<Dayjs>()
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [copiedInfo, setCopiedInfo] = useState(false)
  const [dateError, setDateError] = useState(false)
  const [encriptionVal, setEncriptionVal] = useState('AES-256-CBC')
  const [shaVal, setShaVal] = useState('SHA-256')
  const [schema, setSchema] = useState('PBES2')
  const [password, setPassword] = useState('')
  const [pkc12Bundle, setPkcs12Bundle] = useState<any>(false)
  const attributes = 'RSA 2048'
  const [downloadedCert, setDownloadedCert] = useState(true)
  const [disableBtn, setDisableBtn] = useState(true)

  const [keyType, keyValue] = attributes.split(' ')

  const params: ISort = { limit: LIMIT_MAX, offset: 0 }
  params.sort = [
    {
      columnKey: `name:asc`
    }
  ]

  const pki = new PKI()
  const encriptionOptions = [
    {
      value: 'AES-256-CBC',
      label: translate('clientAuth.encription.aes') as string
    },
    {
      value: 'DES-EDE3-CBC',
      label: translate('clientAuth.encription.des') as string
    }
  ]

  const shaOptions = [
    {
      value: 'SHA-256',
      label: translate('clientAuth.signatureHashAlgorithm.sha256') as string
    },
    {
      value: 'SHA-1',
      label: translate('clientAuth.signatureHashAlgorithm.sha1') as string
    }
  ]

  useEffect(() => {
    setName('')
    setDate(undefined)
    setEncriptionVal('AES-256-CBC')
    setShaVal('SHA-256')
    setPassword(generatePassword(12))
    setPkcs12Bundle(false)
    setNameMaxLengthError(false)
    setDownloadedCert(true)
    setNameUniqueError(false)
    setDateError(false)
    setDisableBtn(true)
    setCopiedInfo(false)
    setShowPasswordModal(false)
  }, [onCancel])

  const handleCancel = (e) => {
    e.preventDefault()
    if (onCancel) {
      onCancel()
    }
  }

  useEffect(() => {
    setPassword(generatePassword(12))
  }, [])

  const submit = (e) => {
    e.preventDefault()
    setShowPasswordModal(true)
  }

  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
    if (
      certificates &&
      certificates.find(
        (cert) => cert.name.toLowerCase().trim() === event.target.value.toLowerCase().trim() && cert.user_id === user.id
      )
    ) {
      setNameUniqueError(true)
    } else {
      setNameUniqueError(false)
    }

    if (event.target.value.length > 255) {
      setNameMaxLengthError(true)
    } else {
      setNameMaxLengthError(false)
    }
  }

  const validateName = useCallback(() => {
    if (nameUniqueError) {
      return translate('common.form.validation.clientAuthCertUnique') as string
    }

    if (nameMaxLengthError) {
      return translate('notifications.errors.maxLengthError') as string
    }
  }, [nameUniqueError, nameMaxLengthError])

  const generateP12Bundle = async (cert, pkcs8) => {
    const certsArray = [cert]
    try {
      const pkcs12 = await pki.generatePKCS12(certsArray, pkcs8, password, encriptionVal, shaVal, schema)
      setPkcs12Bundle(pkcs12)
      setDownloadedCert(false)
      // Trigger download directly without relying on the rendered anchor,
      // since React state updates are asynchronous and the DOM hasn't re-rendered yet.
      const tempLink = document.createElement('a')
      tempLink.href = `data:application/x-pkcs12;base64,` + pkcs12
      tempLink.download = 'Certificate_pkcs12.p12'
      document.body.appendChild(tempLink)
      tempLink.click()
      document.body.removeChild(tempLink)
    } catch (error) {
      console.log(error)
    }
  }

  const getUserCertificate = async (): Promise<void> => {
    const hashAlg = 'SHA-256'
    pki.generatePrivateKeyAndCSR(keyType, hashAlg, {
      type: keyType,
      value: keyValue
    })
      .then((result: any) => {
        const payload = {
          name: name,
          end_date: dayjs.utc(date).toISOString(),
          csr: result.csr
        }
        // *** get X.509 certificate ***
        fetchCertificate(payload, user.id, 'standard')
          .then((cert: any) => {
            if (result && result.pkcs8 && cert && cert?.x509_cert) {
              generateP12Bundle(cert?.x509_cert, result.pkcs8)
            }
          })
      })
      .catch((error) => {
        console.log(error)
      })
  }

  const onDateChange = useCallback((value, dateString) => {
    if (user?.expiry_date) {
      if (
        dayjs
          .utc(value)
          .isBefore(dayjs.utc(user?.expiry_date).add(1, 'day'))
      ) {
        setDateError(false)
      } else {
        setDateError(true)
      }
    }
    setDate(value);
  }, [date]);

  const onEncriptionChange = (val: any): void => {
    setEncriptionVal(val)
    if (val === 'DES-EDE3-CBC') {
      setSchema('PBES1')
    }
  }

  const onShaChange = (sha: any): void => {
    setShaVal(sha)
  }

  const validateDate = useCallback(() => {
    if (date === null) return translate('common.form.validation.required') as string
  }, [date])

  const copyToClipboard = (): void => {
    navigator.clipboard.writeText(password)
      .then(() => {
        setDisableBtn(false)
        setCopiedInfo(true)
        setTimeout(() => setCopiedInfo(false), 1000)
      })
      .catch(err => {
        console.error('Failed to copy to clipboard: ', err)
      })
  }

  const disabledDate = useCallback(
    (current) => {
      // Can not select days before today and today, or later than the user end date if have one
      if (user && user.expiry_date) {
        return (
          (current && current < dayjs().endOf('day')) ||
          current > dayjs(user.expiry_date)
        )
      }

      return current && current < dayjs().endOf('day')
    },
    [user]
  )


  const getDownloadLink = (): ReactNode => {
    const href = `data:application/x-pkcs12;base64,` + pkc12Bundle
    return (
      <a href={href} download={`Certificate_pkcs12.p12`} id='cert_download'>
        <Translate id='clientAuth.downloadMessage' />
      </a>
    )
  }

  const updateCerts = () => {
    if (onCancel) {
      onCancel()
    }
    fetchClientAuthCerts(params, user?.id).then(
      (response: any) => {
        if (response) {
          setCertificates(response.items)
        }
      }
    )
  }

  const close = () => {
    if (onCancel) {
      onCancel()
    }
    setShowPasswordModal(false)
  }

  return (
    <>
      <div className={styles.editServices}>
        <Form center={false} onSubmit={(e) => submit(e)}>

          <FormGroup>
            <Input
              name='certificate_name'
              label={<Translate id='common.form.name' />}
              required
              value={name}
              pattern='^[\w \-]*$'
              errors={{
                valueMissing: translate('common.form.validation.required') as string,
                patternMismatch: translate('notifications.errors.invalidFriendlyNameError') as string
              }}
              className={styles.apiName}
              onChange={onNameChange}
              validate={validateName}
            />
          </FormGroup>
          <FormGroup>
            <DatePicker
              label={translate('common.form.endDate')  as string}
              help={
                user && user.expiry_date
                  ? `${translate('common.form.endDateHelp') as string}, ${dayjs
                    .utc(user.expiry_date)
                    .format('DD-MMM-YYYY')}.`
                  : ''
              }
              onChange={onDateChange}
              disabledDate={disabledDate}
              validate={validateDate}
              value={date}
              id='client_auth_datePicker'
            />
          </FormGroup>
          <FormGroup>
            <Select
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
              size={FormFieldSize.XXL}
              allowClear
              defaultValue={translate('clientAuth.encription.aes') as string}
              value={encriptionVal}
              label={translate('clientAuth.encription.title') as string}
              onChange={onEncriptionChange}
              options={encriptionOptions}
              errors={{ valueMissing: translate('common.form.validation.required') as string }}
              optionFilterProp="label"
              showSearch
            />
          </FormGroup>


          <FormGroup>
            <Select
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
              size={FormFieldSize.XXL}
              allowClear
              defaultValue={translate('clientAuth.signatureHashAlgorithm.sha256') as string}
              value={shaVal}
              label={translate('clientAuth.signatureHashAlgorithm.title') as string}
              onChange={onShaChange}
              options={shaOptions}
              errors={{ valueMissing: translate('common.form.validation.required') as string }}
              optionFilterProp="label"
              showSearch
            />
          </FormGroup>

          <div className={styles.formActions}>
            <Button buttonType={ButtonType.SECONDARY} onClick={handleCancel}>
              <Translate id='common.form.buttons.cancelBtn' />
            </Button>
            <SubmitButton disabled={nameMaxLengthError || nameUniqueError || !date || dateError}>
              <Translate id='clientAuth.createBtn' />
            </SubmitButton>
          </div>

        </Form>
      </div>
      {showPasswordModal &&
        <StandardModal
          modalType={ModalTypes.STANDARD}
          headline={downloadedCert ? translate('clientAuth.savePassword') as string : translate('clientAuth.certificateDownloaded') as string}
          display={showPasswordModal}
          buttonLabel={downloadedCert ? translate('enrollment.downloadCertificate') as string : translate('common.form.buttons.closeBtn') as string}
          hideCancelButton={true}
          buttonCancelLabel=''
          disableButton={disableBtn}
          closable
          onCancelCallBack={downloadedCert ? close : updateCerts}
          onOkCallBack={downloadedCert ? getUserCertificate : updateCerts}
        >
          {downloadedCert && <>
            <p>{translate('clientAuth.passwordNote') as string}</p>
            <Input
              name='certificate_password'
              label={<Translate id='clientAuth.certPassword' />}
              value={password}
              className={styles.apiName}
              readOnly
            />
            {copiedInfo && (
              <div className={styles.copiedContainer} onClick={copyToClipboard}>
                <FontAwesomeIcon size='lg' icon={faCheck as IconProp} className={styles.clipBoardIcon} /><p>{translate('apiTokens.copied') as string}</p>
              </div>

            )
            }
            {!copiedInfo && (
              <div className={styles.copyContainer} onClick={copyToClipboard}>
                <FontAwesomeIcon size='lg' icon={faCopy as IconProp} className={styles.clipBoardIcon} /> <p>{translate('apiTokens.copy') as string}</p>
              </div>

            )
            }
          </>}
          {!downloadedCert && <>
            <p>{translate('clientAuth.generateCertMessage') as string}</p>
            {getDownloadLink()}
          </>
          }
        </StandardModal>
      }
    </>
  )
}

export default connect(
  (state: IAppState) => {
    return {
    }
  },
  {
    fetchClientAuthCerts,
    fetchCertificate
  }
)(CreateClientAuthCertificate)
